import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Text, ScrollView, Alert, ActivityIndicator } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { User } from '../../types';
import { calculateBMI } from '../../utils/validation';
import { colors, typography, spacing } from '../../constants/theme';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Card from '../../components/Card';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainStackParamList, 'ProfileSetup'>;

const ProfileSetupScreen = ({ navigation }: Props) => {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [visceralFat, setVisceralFat] = useState('');
  const [bodyFatPercentage, setBodyFatPercentage] = useState('');
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  // Cargar datos existentes del usuario cuando se monte el componente
  useEffect(() => {
    const loadUserData = async () => {
      setLoading(true);
      try {
        const userId = auth.currentUser?.uid;
        
        if (!userId) {
          throw new Error('Usuario no autenticado');
        }
        
        const userRef = doc(firestore, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          
          // Establecer los valores existentes si están disponibles
          if (userData.height) setHeight(userData.height.toString());
          if (userData.weight) setWeight(userData.weight.toString());
          if (userData.visceralFat) setVisceralFat(userData.visceralFat.toString());
          if (userData.bodyFatPercentage) setBodyFatPercentage(userData.bodyFatPercentage.toString());
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        Alert.alert('Error al cargar perfil', errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  const handleSaveProfile = async () => {
    if (!height || !weight) {
      Alert.alert('Error', 'Por favor ingresa al menos tu altura y peso');
      return;
    }

    setLoading(true);
    
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const heightNum = parseFloat(height);
      const weightNum = parseFloat(weight);
      const bmi = calculateBMI(weightNum, heightNum);
      
      const userData = userDoc.data() as User;
      
      // Update user data with new measurements
      const updatedUserData = {
        ...userData,
        height: heightNum,
        weight: weightNum,
        ...(visceralFat ? { visceralFat: parseFloat(visceralFat) } : {}),
        ...(bodyFatPercentage ? { bodyFatPercentage: parseFloat(bodyFatPercentage) } : {}),
        bmi,
      };
      
      await setDoc(userRef, updatedUserData, { merge: true });
      
      // Navigate to the main tabs after profile setup
      navigation.replace('MainTabs');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      Alert.alert('Error al guardar perfil', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
      <View style={styles.container}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Configuración de Perfil</Text>
        <Text style={[styles.subtitle, { color: theme.colors.grey.dark }]}>
          Por favor ingresa tus datos para personalizar tu experiencia
        </Text>

        {loading && !height ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={[styles.loadingText, { color: theme.colors.text }]}>Cargando datos del perfil...</Text>
          </View>
        ) : (
          <>
            <Card style={styles.card}>
              <Input
                label="Altura (cm)"
                value={height}
                onChangeText={setHeight}
                placeholder="Ej: 175"
                keyboardType="numeric"
              />
              
              <Input
                label="Peso (kg)"
                value={weight}
                onChangeText={setWeight}
                placeholder="Ej: 70"
                keyboardType="numeric"
              />
              
              <Input
                label="Grasa visceral (opcional)"
                value={visceralFat}
                onChangeText={setVisceralFat}
                placeholder="Ej: 10"
                keyboardType="numeric"
              />
              
              <Input
                label="Porcentaje de grasa corporal (opcional)"
                value={bodyFatPercentage}
                onChangeText={setBodyFatPercentage}
                placeholder="Ej: 15"
                keyboardType="numeric"
              />
            </Card>

            <View style={styles.buttonContainer}>
              <Button
                title="Guardar Perfil"
                onPress={handleSaveProfile}
                loading={loading}
                size="large"
              />
              
              <Button
                title="Omitir por ahora"
                onPress={() => navigation.replace('MainTabs')}
                type="text"
                disabled={loading}
              />
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.xl,
    textAlign: 'center',
  },
  card: {
    marginBottom: spacing.xl,
  },
  buttonContainer: {
    gap: spacing.md,
  },
  loaderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
  },
});

export default ProfileSetupScreen; 